/**
 * Vec2 and the collision / geometry helpers the rules engine, server
 * simulation and client prediction share (DESIGN 7.3 math.ts).
 *
 * Conventions
 * - World space: x right, y DOWN (canvas), 1 unit = UNIT_PX pixels.
 * - Angles are radians, measured from +x toward +y (so clockwise on screen),
 *   normalised to (-PI, PI] by `wrapAngle`. The only degree parameters are
 *   the ones named `...Deg` (spread/half-angles that DESIGN quotes in degrees).
 * - Every function is pure and returns fresh objects; callers that need
 *   mutation copy fields themselves. Hot paths avoid temporaries.
 * - Segments are `[start, end]` tuples; `t` parameters are 0 at start, 1 at end.
 */

export interface Vec2 { x: number; y: number }
export type Segment = [Vec2, Vec2];

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;
export const EPS = 1e-9;

export function degToRad(d: number): number { return d * DEG; }
export function radToDeg(r: number): number { return r / DEG; }

export function v(x: number, y: number): Vec2 { return { x, y }; }
export function add(a: Vec2, b: Vec2): Vec2 { return { x: a.x + b.x, y: a.y + b.y }; }
export function sub(a: Vec2, b: Vec2): Vec2 { return { x: a.x - b.x, y: a.y - b.y }; }
export function mul(a: Vec2, k: number): Vec2 { return { x: a.x * k, y: a.y * k }; }
export function dot(a: Vec2, b: Vec2): number { return a.x * b.x + a.y * b.y; }
/** 2D cross product (z component). Positive when b is clockwise of a on screen. */
export function cross(a: Vec2, b: Vec2): number { return a.x * b.y - a.y * b.x; }
export function len(a: Vec2): number { return Math.hypot(a.x, a.y); }
export function lenSq(a: Vec2): number { return a.x * a.x + a.y * a.y; }
export function dist(a: Vec2, b: Vec2): number { return Math.hypot(b.x - a.x, b.y - a.y); }
export function distSq(a: Vec2, b: Vec2): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  return dx * dx + dy * dy;
}

/** Unit vector in the direction of `a`; the zero vector stays zero (no NaN). */
export function norm(a: Vec2): Vec2 {
  const l = Math.hypot(a.x, a.y);
  return l > 0 ? { x: a.x / l, y: a.y / l } : { x: 0, y: 0 };
}

/** Angle of the vector from -> to. (0,0) -> 0. */
export function angleTo(from: Vec2, to: Vec2): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

export function fromAngle(ang: number, length = 1): Vec2 {
  return { x: Math.cos(ang) * length, y: Math.sin(ang) * length };
}

/** Normalise any angle to (-PI, PI]. */
export function wrapAngle(a: number): number {
  return a - TAU * Math.ceil((a - Math.PI) / TAU);
}

/**
 * Signed shortest rotation that takes `from` to `to`, in (-PI, PI].
 * `from + angleDiff(from, to)` points the same way as `to`.
 */
export function angleDiff(from: number, to: number): number {
  return wrapAngle(to - from);
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

export function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

/** Shortest-arc angle interpolation (DESIGN 5.6 entity interpolation). */
export function lerpAngle(a: number, b: number, t: number): number {
  return wrapAngle(a + angleDiff(a, b) * t);
}

export function lerpVec(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

// ------------------------------------------------------------------ colliders

/** Circles overlap (touching counts). */
export function circleCircle(a: Vec2, ra: number, b: Vec2, rb: number): boolean {
  const r = ra + rb;
  return distSq(a, b) <= r * r;
}

export interface SegCircleHit {
  hit: boolean;
  /** Entry parameter along p0->p1 (0 when p0 starts inside). 1 on a miss. */
  t: number;
  /** Entry point. p1 on a miss. */
  point: Vec2;
}

/**
 * First intersection of segment p0->p1 with the circle (c, r). This is the
 * raycast used for beams, shards and arrows (DESIGN 2.8, 5.5): the earliest
 * point where the segment enters the circle. A segment starting inside (or
 * on) the circle hits at t = 0; a tangent touch counts as a hit.
 */
export function segCircle(p0: Vec2, p1: Vec2, c: Vec2, r: number): SegCircleHit {
  const fx = p0.x - c.x, fy = p0.y - c.y;
  const cc = fx * fx + fy * fy - r * r;
  if (cc <= 0) return { hit: true, t: 0, point: { x: p0.x, y: p0.y } };
  const dx = p1.x - p0.x, dy = p1.y - p0.y;
  const a = dx * dx + dy * dy;
  if (a === 0) return { hit: false, t: 1, point: { x: p1.x, y: p1.y } };
  const b = 2 * (fx * dx + fy * dy);
  const disc = b * b - 4 * a * cc;
  if (disc < 0) return { hit: false, t: 1, point: { x: p1.x, y: p1.y } };
  // p0 is outside, so both roots share a sign: the smaller one is the entry.
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  if (t < 0 || t > 1) return { hit: false, t: 1, point: { x: p1.x, y: p1.y } };
  return { hit: true, t, point: { x: p0.x + dx * t, y: p0.y + dy * t } };
}

/**
 * Intersection point of closed segments a0->a1 and b0->b1, or null when they
 * do not cross. Parallel and collinear segments return null even if they
 * overlap (beam merge/cross checks want a single point, DESIGN 2.8).
 */
export function segSeg(a0: Vec2, a1: Vec2, b0: Vec2, b1: Vec2): Vec2 | null {
  const rx = a1.x - a0.x, ry = a1.y - a0.y;
  const sx = b1.x - b0.x, sy = b1.y - b0.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const qx = b0.x - a0.x, qy = b0.y - a0.y;
  const t = (qx * sy - qy * sx) / denom;
  const u = (qx * ry - qy * rx) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { x: a0.x + rx * t, y: a0.y + ry * t };
}

/** Point on segment a->b nearest to p. */
export function closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const abx = b.x - a.x, aby = b.y - a.y;
  const l2 = abx * abx + aby * aby;
  if (l2 === 0) return { x: a.x, y: a.y };
  const t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / l2, 0, 1);
  return { x: a.x + abx * t, y: a.y + aby * t };
}

/** Distance from p to segment a->b (storm thickness, barrier push-out, DESIGN 5.5). */
export function pointSegDist(p: Vec2, a: Vec2, b: Vec2): number {
  const q = closestPointOnSegment(p, a, b);
  return Math.hypot(p.x - q.x, p.y - q.y);
}

/** An axis-aligned box: top-left corner (x, y), width and height (level walls, rooms, doors). */
export interface Rect { x: number; y: number; w: number; h: number }

/** Is p inside the box grown by `pad` on every side (edges inclusive)? */
export function inRect(p: Vec2, b: Rect, pad = 0): boolean {
  return p.x >= b.x - pad && p.x <= b.x + b.w + pad && p.y >= b.y - pad && p.y <= b.y + b.h + pad;
}

/** Point of the box nearest to p (p itself when inside). */
export function closestPointOnRect(p: Vec2, b: Rect): Vec2 {
  return { x: clamp(p.x, b.x, b.x + b.w), y: clamp(p.y, b.y, b.y + b.h) };
}

/**
 * First point where segment p0->p1 enters the box grown by `pad` (slab
 * test), or null when it misses. A segment starting inside hits at t = 0.
 * Growing the box by a disc's radius is how a moving disc is swept against a
 * wall; the corners come out square, a little generous, which is fine for
 * projectiles and sight lines.
 */
export function segRect(p0: Vec2, p1: Vec2, b: Rect, pad = 0): { t: number; point: Vec2 } | null {
  const x0 = b.x - pad, x1 = b.x + b.w + pad, y0 = b.y - pad, y1 = b.y + b.h + pad;
  const dx = p1.x - p0.x, dy = p1.y - p0.y;
  let t0 = 0, t1 = 1;
  if (dx === 0) {
    if (p0.x < x0 || p0.x > x1) return null;
  } else {
    let ta = (x0 - p0.x) / dx, tb = (x1 - p0.x) / dx;
    if (ta > tb) { const s = ta; ta = tb; tb = s; }
    if (ta > t0) t0 = ta;
    if (tb < t1) t1 = tb;
    if (t0 > t1) return null;
  }
  if (dy === 0) {
    if (p0.y < y0 || p0.y > y1) return null;
  } else {
    let ta = (y0 - p0.y) / dy, tb = (y1 - p0.y) / dy;
    if (ta > tb) { const s = ta; ta = tb; tb = s; }
    if (ta > t0) t0 = ta;
    if (tb < t1) t1 = tb;
    if (t0 > t1) return null;
  }
  return { t: t0, point: { x: p0.x + dx * t0, y: p0.y + dy * t0 } };
}

/**
 * Is p inside the cone with its apex at `apex`, centre direction `ang`
 * (radians), half-angle `half` (radians) and length `range`? Boundaries are
 * inclusive (with EPS slack so a point exactly on the edge counts); the apex
 * itself is inside. Used for sprays, wind and melee swings (DESIGN 2.5).
 */
export function pointInCone(p: Vec2, apex: Vec2, ang: number, half: number, range: number): boolean {
  const dx = p.x - apex.x, dy = p.y - apex.y;
  const d2 = dx * dx + dy * dy;
  if (d2 > range * range) return false;
  if (d2 === 0) return true;
  return Math.abs(angleDiff(ang, Math.atan2(dy, dx))) <= half + EPS;
}

// ------------------------------------------------------------------ hazard geometry (DESIGN 2.5 barrier/wall/mines/storm)

/**
 * `n` chords approximating an arc of radius `r` centred on `center`, facing
 * `ang` (radians) and spanning `spreadDeg` degrees. The first point sits at
 * ang - spread/2 and the last at ang + spread/2. n <= 0 yields [].
 */
export function arcSegments(center: Vec2, ang: number, spreadDeg: number, r: number, n: number): Segment[] {
  const segs: Segment[] = [];
  if (n <= 0) return segs;
  const spread = spreadDeg * DEG;
  const a0 = ang - spread / 2;
  const step = spread / n;
  let prev: Vec2 = { x: center.x + Math.cos(a0) * r, y: center.y + Math.sin(a0) * r };
  for (let i = 1; i <= n; i++) {
    const a = a0 + step * i;
    const next: Vec2 = { x: center.x + Math.cos(a) * r, y: center.y + Math.sin(a) * r };
    segs.push([prev, next]);
    prev = next;
  }
  return segs;
}

/**
 * Closed ring of `n` chords at radius `r`; vertex i sits at angle TAU*i/n and
 * the last chord returns to vertex 0. n <= 0 yields [].
 */
export function ringSegments(center: Vec2, r: number, n: number): Segment[] {
  const segs: Segment[] = [];
  if (n <= 0) return segs;
  const pts: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = (TAU * i) / n;
    pts.push({ x: center.x + Math.cos(a) * r, y: center.y + Math.sin(a) * r });
  }
  for (let i = 0; i < n; i++) {
    segs.push([pts[i] as Vec2, pts[(i + 1) % n] as Vec2]);
  }
  return segs;
}

/**
 * A straight wall of total `length` centred on `center` and PERPENDICULAR to
 * `ang`: a line cast at aim angle `ang` stands across the aim like a shield
 * wall (DESIGN 2.4 "Wall (line)"). Split into `n` collinear pieces (default
 * 1) so storms/mines can space along it. Callers wanting a segment along the
 * aim (fissure, thrust) pass `ang + PI/2`.
 */
export function lineSegments(center: Vec2, ang: number, length: number, n = 1): Segment[] {
  const segs: Segment[] = [];
  if (n <= 0) return segs;
  const dx = -Math.sin(ang), dy = Math.cos(ang); // perpendicular to aim
  const half = length / 2;
  let prev: Vec2 = { x: center.x - dx * half, y: center.y - dy * half };
  for (let i = 1; i <= n; i++) {
    const d = -half + (length * i) / n;
    const next: Vec2 = { x: center.x + dx * d, y: center.y + dy * d };
    segs.push([prev, next]);
    prev = next;
  }
  return segs;
}
